const apiHeaderJson = { "Content-Type": "application/json" };

const resolvedApiBase = import.meta.env.VITE_API_BASE;
// const resolvedApiBase = "https://api.dev.zeqi.ai";
// const resolvedApiBase = "http://localhost:3001";

export const API_BASE = resolvedApiBase.replace(/\/+$/, "");

const API_GET_CACHE_PREFIX = "zeqi:api:get:";
const API_GET_CACHE_VERSION = "v1";
const API_DEFAULT_TTL_MS = 3 * 60 * 1000;
const API_ASSESSMENT_TTL_MS = 5 * 60 * 1000;
const memoryGetCache = new Map();
const inflightGetRequests = new Map();

const buildAuthHeaders = (token) =>
  token ? { Authorization: `Bearer ${token}` } : {};

const nowIso = () => new Date().toISOString();

const logWebChatApi = (event, payload = {}) => {
  console.log(`[reports-chat][frontend][${nowIso()}] ${event}`, payload);
};

const now = () => Date.now();

const buildCacheKey = (scope, parts = []) =>
  [API_GET_CACHE_PREFIX, API_GET_CACHE_VERSION, scope, ...parts.map((part) => String(part ?? ""))].join(":");

const readCachedValue = (key) => {
  const memEntry = memoryGetCache.get(key);
  if (memEntry && memEntry.expiresAt > now()) {
    return memEntry.value;
  }
  if (memEntry) {
    memoryGetCache.delete(key);
  }

  try {
    const raw = window.sessionStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || parsed.expiresAt <= now()) {
      window.sessionStorage.removeItem(key);
      return null;
    }
    memoryGetCache.set(key, parsed);
    return parsed.value;
  } catch {
    return null;
  }
};

const writeCachedValue = (key, value, ttlMs) => {
  const entry = { value, expiresAt: now() + ttlMs };
  memoryGetCache.set(key, entry);
  try {
    window.sessionStorage.setItem(key, JSON.stringify(entry));
  } catch {
    // ignore storage failures
  }
  return value;
};

const getCachedJson = async ({ key, ttlMs = API_DEFAULT_TTL_MS, loader }) => {
  const cached = readCachedValue(key);
  if (cached !== null && cached !== undefined) {
    return cached;
  }

  const existing = inflightGetRequests.get(key);
  if (existing) {
    return existing;
  }

  const request = Promise.resolve()
    .then(loader)
    .then((value) => writeCachedValue(key, value, ttlMs))
    .finally(() => {
      inflightGetRequests.delete(key);
    });

  inflightGetRequests.set(key, request);
  return request;
};

export async function fetchCurrentUser(authToken) {
  const res = await fetch(`${API_BASE}/user/me`, {
    method: "GET",
    headers: {
      ...apiHeaderJson,
      ...buildAuthHeaders(authToken),
    },
  });
  if (!res.ok) {
    const err = new Error(`user/me failed (${res.status})`);
    err.status = res.status;
    throw err;
  }
  return res.json();
}

export async function sendInvite(email, authToken) {
  const res = await fetch(`${API_BASE}/zeqi/invitees/send`, {
    method: "POST",
    headers: {
      ...apiHeaderJson,
      ...buildAuthHeaders(authToken),
    },
    body: JSON.stringify({ email }),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || "Unable to send invite.");
  }
  return res.text();
}

export async function sendChatMessage(userId, message, sessionId, authToken) {
  const startedAt = performance.now();
  logWebChatApi("send.start", {
    userId,
    sessionId,
    hasAuthToken: Boolean(authToken),
    contentLength: String(message?.content || "").length,
    attachmentCount: Array.isArray(message?.attachments) ? message.attachments.length : 0,
  });
  const res = await fetch(`${API_BASE}/discuss/chat`, {
    method: "POST",
    headers: {
      ...apiHeaderJson,
      ...buildAuthHeaders(authToken),
    },
    body: JSON.stringify({ userId, user_id: userId, sessionId, message }),
  });
  // read the raw text
  const text = await res.text();
  // console.log('[chat] raw response:', text);
  if (!res.ok) {
    logWebChatApi("send.error", {
      userId,
      sessionId,
      status: res.status,
      durationMs: Math.round(performance.now() - startedAt),
    });
    throw new Error(`Chat API error ${res.status}: ${text}`);
  }
  // now parse JSON (or blow up with a useful error)
  try {
    const parsed = JSON.parse(text);
    logWebChatApi("send.success", {
      userId,
      sessionId: parsed?.sessionId || sessionId,
      durationMs: Math.round(performance.now() - startedAt),
      hasAiMessage: Boolean(parsed?.aiMessage),
      limited: Boolean(parsed?.limit),
    });
    return parsed;
  } catch {
    logWebChatApi("send.parse_error", {
      userId,
      sessionId,
      durationMs: Math.round(performance.now() - startedAt),
    });
    throw new Error(`Failed to parse /discuss/chat JSON: "${text}"`);
  }
}

export async function sendVoiceChatMessage({
  userId,
  sessionId,
  audioFile,
  authToken,
}) {
  if (!audioFile) {
    throw new Error("Missing audio recording");
  }
  const formData = new FormData();
  const fileName = audioFile.name || `voice-${Date.now()}.webm`;
  formData.append("audio", audioFile, fileName);
  formData.append("user_id", userId);
  if (sessionId) {
    formData.append("sessionId", sessionId);
  }

  const init = {
    method: "POST",
    body: formData,
  };
  if (authToken) {
    init.headers = { Authorization: `Bearer ${authToken}` };
  }

  const res = await fetch(`${API_BASE}/discuss/chat/voice`, init);
  const text = await res.text();
  if (!res.ok) {
    throw new Error(
      `Voice chat error ${res.status}: ${text || "Unknown error"}`,
    );
  }
  try {
    return JSON.parse(text);
  } catch {
    throw new Error(`Failed to parse voice chat response: "${text}"`);
  }
}

export async function getAiChatHistory(sessionId, authToken) {
  const startedAt = performance.now();
  logWebChatApi("aiHistory.start", {
    sessionId,
    hasAuthToken: Boolean(authToken),
  });
  const url = new URL(`${API_BASE}/discuss/chat/aiHistory`);
  url.searchParams.set("sessionId", sessionId);
  const res = await fetch(url.toString(), {
    method: "GET",
    headers: {
      ...apiHeaderJson,
      ...buildAuthHeaders(authToken),
    },
  });
  const text = await res.text();
  // console.log('[chat/aiHistory] raw response:', text);
  if (!res.ok) {
    logWebChatApi("aiHistory.error", {
      sessionId,
      status: res.status,
      durationMs: Math.round(performance.now() - startedAt),
    });
    throw new Error(`AI History API error ${res.status}: ${text}`);
  }
  try {
    const parsed = JSON.parse(text);
    logWebChatApi("aiHistory.success", {
      sessionId,
      durationMs: Math.round(performance.now() - startedAt),
      count: Array.isArray(parsed?.aiMessages) ? parsed.aiMessages.length : null,
    });
    return parsed;
  } catch {
    logWebChatApi("aiHistory.parse_error", {
      sessionId,
      durationMs: Math.round(performance.now() - startedAt),
    });
    throw new Error(`Failed to parse /discuss/chat/aiHistory JSON: "${text}"`);
  }
}

export async function getHumanChatHistory(sessionId, authToken) {
  const startedAt = performance.now();
  logWebChatApi("humanHistory.start", {
    sessionId,
    hasAuthToken: Boolean(authToken),
  });
  const url = new URL(`${API_BASE}/discuss/chat/humanHistory`);
  url.searchParams.set("sessionId", sessionId);

  const res = await fetch(url.toString(), {
    method: "GET",
    headers: {
      ...apiHeaderJson,
      ...buildAuthHeaders(authToken),
    },
  });

  const text = await res.text();
  // console.log('[chat/humanHistory] raw response:', text);

  if (!res.ok) {
    logWebChatApi("humanHistory.error", {
      sessionId,
      status: res.status,
      durationMs: Math.round(performance.now() - startedAt),
    });
    throw new Error(`Human History API error ${res.status}: ${text}`);
  }

  try {
    const parsed = JSON.parse(text);
    logWebChatApi("humanHistory.success", {
      sessionId,
      durationMs: Math.round(performance.now() - startedAt),
      count: Array.isArray(parsed?.humanMessages) ? parsed.humanMessages.length : null,
    });
    return parsed;
  } catch {
    logWebChatApi("humanHistory.parse_error", {
      sessionId,
      durationMs: Math.round(performance.now() - startedAt),
    });
    throw new Error(
      `Failed to parse /discuss/chat/humanHistory JSON: "${text}"`,
    );
  }
}

export async function fetchFoundationalBusinessRealitySummary({
  userId,
  shopId,
  authToken,
}) {
  if (!userId) {
    throw new Error("userId is required");
  }

  const url = new URL(`${API_BASE}/shop/foundational-business-reality-summary`);
  url.searchParams.set("userId", userId);
  if (shopId) url.searchParams.set("shopId", shopId);
  const cacheKey = buildCacheKey("foundational-business-reality-summary", [userId, shopId || ""]);

  return getCachedJson({
    key: cacheKey,
    ttlMs: API_ASSESSMENT_TTL_MS,
    loader: async () => {
      const res = await fetch(url.toString(), {
        method: "GET",
        headers: {
          ...apiHeaderJson,
          ...buildAuthHeaders(authToken),
        },
      });

      const text = await res.text();
      if (!res.ok) {
        throw new Error(
          `Foundational summary fetch failed (${res.status}): ${text || "Unknown error"}`,
        );
      }

      try {
        return JSON.parse(text);
      } catch {
        throw new Error(`Failed to parse foundational summary response: "${text}"`);
      }
    },
  });
}

export async function createFoundationalBusinessRealitySummary({
  userId,
  shopId,
  authToken,
}) {
  if (!userId || !shopId) {
    throw new Error("userId and shopId are required");
  }

  const res = await fetch(`${API_BASE}/shop/create-foundational-business-reality-summary`, {
    method: "POST",
    headers: {
      ...apiHeaderJson,
      ...buildAuthHeaders(authToken),
    },
    body: JSON.stringify({ userId, shopId }),
  });

  const text = await res.text();
  if (!res.ok) {
    throw new Error(
      `Foundational summary create failed (${res.status}): ${text || "Unknown error"}`,
    );
  }

  try {
    return JSON.parse(text);
  } catch {
    throw new Error(`Failed to parse foundational summary create response: "${text}"`);
  }
}

export async function updateShopOnboardingProfile({ step, answer, authToken }) {
  if (!step || !answer) {
    throw new Error("Shop onboarding step and answer are required");
  }

  const res = await fetch(`${API_BASE}/shop/onboarding/profile`, {
    method: "POST",
    headers: {
      ...apiHeaderJson,
      ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
    },
    body: JSON.stringify({ step, answer }),
  });

  const text = await res.text();
  if (!res.ok) {
    throw new Error(
      `Onboarding profile update failed (${res.status}): ${text}`,
    );
  }

  try {
    return JSON.parse(text);
  } catch {
    throw new Error(`Failed to parse onboarding response: "${text}"`);
  }
}

export const CLUSTER_ALL_ENDPOINT = {
  url: `${API_BASE}/clusters/all`,
  method: "POST",
};
export const CLUSTER_ONBOARDING_ENDPOINT = {
  url: `${API_BASE}/clusters/onboarding`,
  method: "POST",
};
export const CLUSTER_FOUNDATION_STAGE_ENDPOINT = {
  url: `${API_BASE}/clusters/foundation-stage`,
  method: "POST",
};
export const CLUSTER_FUNNEL_STAGE_ENDPOINT = {
  url: `${API_BASE}/clusters/funnel-stage`,
  method: "POST",
};
export const CLUSTER_TOP_OF_FUNNEL_ENDPOINT = {
  url: `${API_BASE}/clusters/top-of-the-funnel`,
  method: "POST",
};
export const CLUSTER_MIDDLE_OF_FUNNEL_ENDPOINT = {
  url: `${API_BASE}/clusters/middle-of-the-funnel`,
  method: "POST",
};
export const CLUSTER_BOTTOM_OF_FUNNEL_ENDPOINT = {
  url: `${API_BASE}/clusters/bottom-of-the-funnel`,
  method: "POST",
};

export const CLUSTER_ASSIGN_SECONDARY_ENDPOINT = {
  url: `${API_BASE}/clusters/assign-secondary`,
  method: "POST",
};

export const GOOGLE_DATA_CHECKER_ENDPOINT = {
  url: `${API_BASE}/google/data-checker`,
  method: "POST",
};
export const FACEBOOK_DATA_CHECKER_ENDPOINT = {
  url: `${API_BASE}/facebook-data/data-checker`,
  method: "POST",
};

async function postSubscription({ path, token, payload }) {
  const res = await fetch(`${API_BASE}/subscription/${path}`, {
    method: "POST",
    headers: {
      ...apiHeaderJson,
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(payload),
  });

  const text = await res.text();

  if (!res.ok) {
    throw new Error(
      `Subscription request failed (${res.status}): ${text || "Unknown error"}`,
    );
  }

  try {
    return JSON.parse(text);
  } catch {
    throw new Error(`Invalid JSON from subscription response: "${text}"`);
  }
}

export async function applySmsaPromoCode({
  token,
  userId,
  partnerCode,
  successUrl,
  cancelUrl,
  metadata = {},
}) {
  return postSubscription({
    path: "smsa",
    token,
    payload: { userId, partnerCode, successUrl, cancelUrl, metadata },
  });
}

export async function applyGeneralPromoCode({
  token,
  userId,
  promoCode,
  successUrl,
  cancelUrl,
  metadata = {},
}) {
  return postSubscription({
    path: "general",
    token,
    payload: { userId, promoCode, successUrl, cancelUrl, metadata },
  });
}

export async function startGeneralCheckout({
  token,
  userId,
  successUrl,
  cancelUrl,
  trialPeriodDays = 0,
  metadata = {},
}) {
  return postSubscription({
    path: "general",
    token,
    payload: { userId, successUrl, cancelUrl, trialPeriodDays, metadata },
  });
}

export async function startPremiumCheckout({
  token,
  userId,
  successUrl,
  cancelUrl,
  trialPeriodDays = 0,
  premiumTier,
  metadata = {},
}) {
  return postSubscription({
    path: "premium",
    token,
    payload: {
      userId,
      successUrl,
      cancelUrl,
      trialPeriodDays,
      premiumTier,
      metadata,
    },
  });
}

export async function startSmsaCheckout({
  token,
  userId,
  successUrl,
  cancelUrl,
  trialPeriodDays = 0,
  metadata = {},
}) {
  return postSubscription({
    path: "smsa",
    token,
    payload: { userId, successUrl, cancelUrl, trialPeriodDays, metadata },
  });
}

export async function startFreeCheckout({
  token,
  userId,
  successUrl,
  cancelUrl,
  trialPeriodDays = 0,
  metadata = {},
}) {
  return postSubscription({
    path: "free",
    token,
    payload: { userId, successUrl, cancelUrl, trialPeriodDays, metadata },
  });
}

export async function cancelSubscription({ token, userId }) {
  return postSubscription({
    path: "cancel",
    token,
    payload: { userId },
  });
}

export async function startMonthlySubscriptionCheckout({
  token,
  shopId,
  successUrl,
  cancelUrl,
  metadata = {},
}) {
  return postSubscription({
    path: "monthly",
    token,
    payload: { shopId, successUrl, cancelUrl, metadata },
  });
}

/**
 * POST /api/subscription/tiered
 *
 * @param {Object} opts
 * @param {string} opts.token
 * @param {"STARTER"|"PRO"|"PRO_PLUS"} opts.planFamily
 * @param {number} opts.creditTier     e.g. 200, 700, 2000
 * @param {number} [opts.extraSeats]
 * @param {number} [opts.extraBrands]
 * @param {string} [opts.successUrl]
 * @param {string} [opts.cancelUrl]
 */
export async function startTieredCheckout({
  token,
  planFamily,
  creditTier,
  extraSeats = 0,
  extraBrands = 0,
  successUrl,
  cancelUrl,
}) {
  return postSubscription({
    path: "tiered",
    token,
    payload: { planFamily, creditTier, extraSeats, extraBrands, successUrl, cancelUrl },
  });
}

export async function submitFeatureInterest({ email, feature }) {
  const res = await fetch(`${API_BASE}/zeqi/email/feature-interest`, {
    method: "POST",
    headers: apiHeaderJson,
    body: JSON.stringify({ email, feature }),
  });
  const text = await res.text();
  if (!res.ok) {
    throw new Error(`Feature interest submit failed (${res.status}): ${text || "Unknown error"}`);
  }
  try {
    return JSON.parse(text);
  } catch {
    throw new Error(`Failed to parse feature interest response: "${text}"`);
  }
}

export async function getReportAccess({ token, shopId }) {
  const url = new URL(`${API_BASE}/subscription/access`);
  if (shopId) url.searchParams.set("shopId", shopId);

  const res = await fetch(url.toString(), {
    method: "GET",
    headers: {
      ...apiHeaderJson,
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });

  const text = await res.text();
  if (!res.ok) {
    throw new Error(
      `Report access fetch failed (${res.status}): ${text || "Unknown error"}`,
    );
  }

  try {
    return JSON.parse(text);
  } catch {
    throw new Error(`Failed to parse report access response: "${text}"`);
  }
}
